import { trace } from 'owa-trace';
import { HttpReadyState, isSuccessStatusCode } from 'owa-http-status-codes';
import { parseResponse } from './parseResponse';
import { setHeadersOnRequest } from './setHeadersOnRequest';
import {
    addSubscription,
    removeSubscription,
    getSubscriptionsCount,
    notifySubscribers,
    createSubscriptionCache,
    SubscriptionsCache,
} from './subscriptions';
import {
    createRetriableFunction,
    createExponentialBackoffFunction,
    RetriableFunction,
} from 'owa-retriable-function';
import { TaskSubscriptionData } from '../schema/TaskSubscriptionData';
import { ToDoNotification } from '../schema/ToDoNotification';
import { isToDoNotification } from './typeGuards';

// TODO VSO 79588: Consume todo notifications through the owa notification channel
const TODO_NOTIFICATION_ENDPOINT = 'https://substrate.office.com/todo/api/v1/realtime';
let xhr: XMLHttpRequest;
let currentRequest: RetriableFunction<void> | undefined;

const createRetriableFunc = createRetriableFunction({
    maximumAttempts: 3,
    timeBetweenRetryInMS: createExponentialBackoffFunction(1000),
});

let subCache: SubscriptionsCache<ToDoNotification>;

export async function subscribeNotifications(
    subscriptionId: string,
    subscriptionData: TaskSubscriptionData<ToDoNotification>
) {
    if (!subCache) {
        subCache = createSubscriptionCache<ToDoNotification>();
    }
    addSubscription<ToDoNotification>(subCache, subscriptionId, subscriptionData);
    if (currentRequest) {
        return;
    }
    try {
        currentRequest = createRetriableFunc(() =>
            subscribeInternal(notifications =>
                notifications.forEach(notification => notifySubscribers(subCache, notification))
            )
        );
        await currentRequest.retriableFunc();
    } catch (error) {
        trace.warn(`[owa-todo-notifications] subscribe: subscribe with retry failed: ${error}`);
        currentRequest = undefined;
    }
}

export async function unSubscribeNotifications(subscriptionId: string) {
    if (!subCache) {
        return;
    }
    // remove from the subscriptions list
    removeSubscription(subCache, subscriptionId);

    // if there are not subscriptions in the list, end the connection
    if (getSubscriptionsCount(subCache) === 0) {
        xhr?.abort();
        currentRequest = undefined;
    }
}

function subscribeInternal(
    onNotification: (notification: ToDoNotification[]) => void
): Promise<void> {
    return new Promise((resolve, reject) => {
        xhr = new XMLHttpRequest();

        xhr.onload = ev => {
            if (isSuccessStatusCode(xhr.status)) {
                resolve();
            } else {
                reject();
            }
        };

        xhr.onerror = () => {
            reject();
        };
        xhr.ontimeout = () => {
            reject();
        };

        xhr.open('GET', TODO_NOTIFICATION_ENDPOINT);

        let seenBytes = 0;
        xhr.onreadystatechange = () => {
            if (xhr.readyState > HttpReadyState.HeadersReceived) {
                // The responseText object is additive. So we use seenBytes,
                // to keep track of how much data in responseText we have already processed
                const newData: string = xhr.responseText.substring(seenBytes);
                const { unparsedBytes, parsedItems } = parseResponse(newData);
                seenBytes = xhr.responseText.length - unparsedBytes;
                if (parsedItems) {
                    const todoNotifications = parsedItems.filter(isToDoNotification);
                    onNotification(todoNotifications);
                }
            }
        };

        setHeadersOnRequest(xhr).then(() => {
            xhr.send();
        });
    });
}
